import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Button,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormGroup,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  Assignment as SurveyIcon,
  Warning as WarningIcon,
  Check as CheckIcon,
  Settings as SettingsIcon,
  Delete as DeleteIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  Report as ReportIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const notificationIcons = {
  survey_approved: <CheckIcon color="success" />,
  survey_rejected: <WarningIcon color="error" />,
  survey_paused: <PauseIcon color="warning" />,
  survey_activated: <PlayIcon color="success" />,
  survey_completed: <SurveyIcon color="primary" />,
  new_response: <AssignmentIcon color="info" />,
  moderation_required: <ReportIcon color="warning" />,
  quota_reached: <CheckIcon color="success" />
};

export default function ClientNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState({
    email: {
      survey_approved: true,
      survey_rejected: true,
      new_response: true,
      survey_completed: true,
      quota_reached: true
    },
    push: {
      survey_approved: true,
      survey_rejected: true,
      new_response: true,
      survey_completed: true,
      quota_reached: true
    }
  });

  useEffect(() => {
    fetchNotifications();
    fetchSettings();
  }, []);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(res.data);
    } catch (e) {
      console.error('Failed to load notifications', e);
    }
  };

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      if (!currentUser?.id) return;
      const res = await axios.get(`${API_BASE}/users/${currentUser.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.notificationSettings) setSettings(res.data.notificationSettings);
    } catch (e) {
      // ignore for now, use defaults
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, read: true } : n));
    } catch (e) {
      console.error('Failed to mark as read', e);
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE}/notifications/${notificationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (e) {
      console.error('Failed to delete notification', e);
    }
  };

  const handleSettingChange = (channel, type) => {
    setSettings(prev => ({
      ...prev,
      [channel]: {
        ...prev[channel],
        [type]: !prev[channel][type]
      }
    }));
  };

  const saveSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE}/notifications/settings`, { settings }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettingsOpen(false);
    } catch (e) {
      console.error('Failed to save settings', e);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE}/notifications/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (e) {
      console.error('Failed to mark all as read', e);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return '-';
    
    const now = new Date();
    const diff = now - date;
    
    if (diff < 24 * 60 * 60 * 1000) {
      return date.toLocaleTimeString();
    }
    return date.toLocaleDateString();
  };

  return (
    <Box>
      <Paper 
        elevation={3} 
        sx={{ 
          p: { xs: 2, md: 3 }, 
          mb: 3,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: { xs: '16px', md: '32px' },
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }}
      >
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1.5, sm: 0 },
          mb: 3,
        }}>
          <Typography 
            variant="h5"
            sx={{ 
              fontWeight: 700, 
              color: '#2d3748',
              fontSize: { xs: '1.25rem', md: '1.5rem' }
            }}
          >
            Уведомления
          </Typography>
          <Box sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            width: { xs: '100%', sm: 'auto' },
            justifyContent: { xs: 'space-between', sm: 'flex-end' },
          }}>
            <Button 
              size="small" 
              onClick={handleMarkAllRead}
              sx={{
                color: '#0984e3',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: 'rgba(9, 132, 227, 0.08)'
                }
              }}
            >
              Отметить все прочитанными
            </Button>
            <IconButton 
              onClick={() => setSettingsOpen(true)}
              sx={{
                color: '#718096',
                '&:hover': {
                  backgroundColor: 'rgba(113, 128, 150, 0.1)',
                  color: '#0984e3'
                }
              }}
            >
              <SettingsIcon />
            </IconButton>
          </Box>
        </Box>

        <List>
          {notifications.map((notification, index) => (
            <React.Fragment key={notification.id}>
              {index > 0 && <Divider />}
              <ListItem
                sx={{
                  bgcolor: notification.read ? 'transparent' : 'rgba(116, 185, 255, 0.08)',
                  transition: 'all 0.3s ease',
                  borderRadius: '12px',
                  mb: 0.5,
                  '&:hover': {
                    bgcolor: 'rgba(116, 185, 255, 0.12)'
                  }
                }}
              >
                <ListItemIcon
                  sx={{
                    color: '#0984e3',
                    minWidth: '48px'
                  }}
                >
                  {notificationIcons[notification.type] || <SurveyIcon />}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontWeight: 600, color: '#2d3748' }}>
                        {notification.title}
                      </Typography>
                      {!notification.read && (
                        <Chip
                          label="Новое"
                          size="small"
                          sx={{ 
                            ml: 1,
                            background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
                            color: '#fff',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            height: '24px'
                          }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <>
                      <Typography variant="body2" sx={{ color: '#718096', fontWeight: 500, mt: 0.5 }}>
                        {notification.message}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#a0aec0', mt: 0.5, display: 'block' }}>
                        {formatTimestamp(notification.createdAt)}
                      </Typography>
                    </>
                  }
                />
                <Box>
                  {!notification.read && (
                    <Button
                      size="small"
                      onClick={() => handleMarkAsRead(notification.id)}
                      sx={{
                        color: '#0984e3',
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: '8px',
                        '&:hover': {
                          backgroundColor: 'rgba(9, 132, 227, 0.08)'
                        }
                      }}
                    >
                      Отметить прочитанным
                    </Button>
                  )}
                  <IconButton
                    edge="end"
                    onClick={() => handleDelete(notification.id)}
                    sx={{
                      color: '#d63031',
                      '&:hover': {
                        backgroundColor: 'rgba(214, 48, 49, 0.1)'
                      }
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </ListItem>
            </React.Fragment>
          ))}
          {notifications.length === 0 && (
            <ListItem
              sx={{
                textAlign: 'center',
                flexDirection: 'column',
                py: 4
              }}
            >
              <ListItemText
                primary={
                  <Typography sx={{ fontWeight: 600, color: '#2d3748', fontSize: '1.125rem' }}>
                    Нет уведомлений
                  </Typography>
                }
                secondary={
                  <Typography sx={{ color: '#718096', mt: 1 }}>
                    Вы всё просмотрели!
                  </Typography>
                }
              />
            </ListItem>
          )}
        </List>
      </Paper>

      {/* Notification Settings Dialog */}
      <Dialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: '24px',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#2d3748', fontSize: '1.5rem' }}>
          Настройки уведомлений
        </DialogTitle>
        <DialogContent>
          <Typography 
            variant="h6" 
            gutterBottom 
            sx={{ 
              mt: 2, 
              fontWeight: 700, 
              color: '#2d3748',
              fontSize: '1.125rem'
            }}
          >
            Email уведомления
          </Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.email?.survey_approved || false}
                  onChange={() => handleSettingChange('email', 'survey_approved')}
                />
              }
              label="Одобрение опроса"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.email?.survey_rejected || false}
                  onChange={() => handleSettingChange('email', 'survey_rejected')}
                />
              }
              label="Отклонение опроса"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.email?.new_response || false}
                  onChange={() => handleSettingChange('email', 'new_response')}
                />
              }
              label="Новые ответы на опросы"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.email?.survey_completed || false}
                  onChange={() => handleSettingChange('email', 'survey_completed')}
                />
              }
              label="Завершение опроса"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.email?.quota_reached || false}
                  onChange={() => handleSettingChange('email', 'quota_reached')}
                />
              }
              label="Достижение целевой квоты"
            />
          </FormGroup>

          <Typography 
            variant="h6" 
            gutterBottom 
            sx={{ 
              mt: 3, 
              fontWeight: 700, 
              color: '#2d3748',
              fontSize: '1.125rem'
            }}
          >
            Push уведомления
          </Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.push?.survey_approved || false}
                  onChange={() => handleSettingChange('push', 'survey_approved')}
                />
              }
              label="Одобрение опроса"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.push?.survey_rejected || false}
                  onChange={() => handleSettingChange('push', 'survey_rejected')}
                />
              }
              label="Отклонение опроса"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.push?.new_response || false}
                  onChange={() => handleSettingChange('push', 'new_response')}
                />
              }
              label="Новые ответы на опросы"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.push?.survey_completed || false}
                  onChange={() => handleSettingChange('push', 'survey_completed')}
                />
              }
              label="Завершение опроса"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.push?.quota_reached || false}
                  onChange={() => handleSettingChange('push', 'quota_reached')}
                />
              }
              label="Достижение целевой квоты"
            />
          </FormGroup>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={() => setSettingsOpen(false)}
            sx={{
              color: '#718096',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'rgba(113, 128, 150, 0.1)'
              }
            }}
          >
            Отмена
          </Button>
          <Button 
            onClick={saveSettings} 
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
              color: '#fff',
              fontWeight: 600,
              borderRadius: '12px',
              textTransform: 'none',
              px: 3,
              boxShadow: '0 4px 14px rgba(9, 132, 227, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0984e3 0%, #74b9ff 100%)',
                boxShadow: '0 6px 20px rgba(9, 132, 227, 0.6)'
              }
            }}
          >
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
